import { dictionarySection } from "@/content/i18n/dictionaries";
import type { Locale } from "@/lib/i18n/config";

type FormsCopy = {
  primitive: {
    checkFieldsError: string;
    genericError: string;
    selectOneLabel: string;
    defaultDone: string;
    thankYouEyebrow: string;
    modalCloseDialogLabel: string;
    modalCloseLabel: string;
    invalidOptionFallback: string;
  };
  validationMessages: Record<string, string>;
  waitlist: {
    successMessage: string;
    confirmationEmailNotice: string;
    confirmLinkLabel: string;
    duplicateMessage: string;
    emailDispatchFailedMessage: string;
    emailLabel: string;
    emailPlaceholder: string;
    nameLabel: string;
    namePlaceholder: string;
    stackedSubmitLabel: string;
    inlineSubmitLabel: string;
    busyLabel: string;
    positionTemplate: string;
    consentLabel: string;
    consentTextRecord: string;
  };
  newsletter: {
    successMessage: string;
    emailLabel: string;
    emailPlaceholder: string;
    submitLabel: string;
    busyLabel: string;
    consentLabel: string;
    consentTextRecord: string;
  };
  contact: {
    successMessage: string;
    ticketSuccessMessage: string;
    nameLabel: string;
    namePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    phoneLabel: string;
    phonePlaceholder: string;
    messageLabel: string;
    messagePlaceholder: string;
    consentLabel: string;
    submitLabel: string;
    busyLabel: string;
    consentTextRecord: string;
  };
  collaborator: {
    successMessage: string;
    ticketSuccessPrefix: string;
    ticketCreatedSuffix: string;
    ticketFollowUp: string;
    nameLabel: string;
    emailLabel: string;
    whatYouDoLabel: string;
    messageLabel: string;
    messagePlaceholder: string;
    submitLabel: string;
    busyLabel: string;
    consentLabel: string;
    consentTextRecord: string;
    lanes: { value: "stylist" | "creator" | "retailer" | "other"; label: string }[];
  };
  investorDeck: {
    successMessage: string;
    nameLabel: string;
    workEmailLabel: string;
    fundLabel: string;
    messageLabel: string;
    messagePlaceholder: string;
    submitLabel: string;
    busyLabel: string;
    consentLabel: string;
    consentTextRecord: string;
  };
  partner: {
    successMessage: string;
    ticketSuccessPrefix: string;
    ticketCreatedSuffix: string;
    ticketFollowUp: string;
    nameLabel: string;
    emailLabel: string;
    companyLabel: string;
    partnerTypeLabel: string;
    messageLabel: string;
    messagePlaceholder: string;
    submitLabel: string;
    busyLabel: string;
    consentLabel: string;
    consentTextRecord: string;
    types: { value: "professional" | "business" | "other"; label: string }[];
  };
  modals: {
    contact: { title: string; description: string };
    investorDeck: { title: string; description: string };
    collaborator: { title: string; description: string };
    heroWaitlist: { title: string; description: string };
    floatingWaitlist: { title: string; description: string };
    iosWaitlist: { title: string; description: string };
  };
  floatingSignup: {
    ariaLabel: string;
    title: string;
    subtitle: string;
  };
};

export function getFormsCopy(locale: Locale): FormsCopy {
  return dictionarySection(locale, "forms") as FormsCopy;
}

export function formatWaitlistPosition(locale: Locale, position: number): string {
  const value = position.toLocaleString(locale);
  const template = getFormsCopy(locale).waitlist.positionTemplate;
  return template.replace("{position}", value);
}

export function translateValidationMessage(message: string, locale: Locale): string {
  const mapped = getFormsCopy(locale).validationMessages[message];
  if (mapped) return mapped;
  if (message.toLowerCase().includes("invalid option")) {
    return getFormsCopy(locale).primitive.invalidOptionFallback;
  }
  return message;
}

export function translateApiErrorMessage(
  code: string,
  message: string,
  locale: Locale,
): string {
  const forms = getFormsCopy(locale);
  switch (code) {
    case "waitlist_duplicate":
      return forms.waitlist.duplicateMessage;
    case "confirmation_email_failed":
      return forms.waitlist.emailDispatchFailedMessage;
    case "email_required":
      return forms.validationMessages["Please enter an email"] ?? message;
    case "email_invalid":
      return (
        forms.validationMessages["That doesn't look like a valid email"] ?? message
      );
    default:
      return message;
  }
}
